import { apiClient } from './client';

/**
 * Açılış ekranı ölçüleri.
 *
 * 🔴 `sync_history` türevli alanlar (syncsLast24h/successfulSyncs/failedSyncs/
 * totalRowsSynced/syncSuccessRate) ve `recentSyncs`/`syncTrend`/`agentStatus`
 * 2026-08-19'da KALDIRILDI: canlıda `sync_history` toplam 0 satırdı ve
 * `syncSuccessRate` sıfır senkronu "%100 başarı" diye raporluyordu. Ölçü artık
 * CDC aynasının canlı işaretinden (`cdc_sync_state.updated_at`) geliyor.
 */
export interface DashboardMetrics {
  summary: {
    totalFirms: number;
    totalDataSources: number;
    totalApiKeys: number;
    errorLogsLast24h: number;
    /** CDC aynası kullanan aktif firma sayısı. */
    cdcFirmalar: number;
    cdcTazeAyna: number;
    /** Bayat + hiç senkron olmamış. Ekranın tek eyleme çağıran sayacı. */
    cdcBayatAyna: number;
    /** Eski nesil RaporGör agent'ı — kapsamı dar, ayrı alan. */
    eskiAgentToplam: number;
    eskiAgentOnline: number;
  };
  /** En bayat üstte (sıralama sunucuda). */
  aynaTazeligi: AynaSatiri[];
}

export interface AynaSatiri {
  firmId: number;
  firma: string;
  durum: 'hic' | 'taze' | 'gecikmis' | 'bayat';
  sonSenkronUtc: string | null;
  yasSaniye: number | null;
}

export interface FirmMetrics {
  firm: {
    id: number;
    name: string;
    subdomain: string;
    isActive: boolean;
  };
  agents: {
    id: number;
    name: string;
    isOnline: boolean;
    lastSeenAt: string | null;
    isActive: boolean;
  }[];
  dataSources: {
    id: number;
    name: string;
    sourceType: string;
    isActive: boolean;
    lastSyncAt: string | null;
    syncsLast24h: number;
    lastSyncStatus: string | null;
  }[];
  syncHistory: {
    date: string;
    total: number;
    success: number;
    failed: number;
    rowsSynced: number;
  }[];
  logSummary: { level: string; count: number }[];
  apiKeyUsage: {
    id: number;
    name: string;
    isActive: boolean;
    lastUsedAt: string | null;
  }[];
}

export interface SyncHistoryResponse {
  data: SyncHistoryItem[];
  pagination: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
}

export interface SyncHistoryItem {
  id: number;
  firmId: number;
  firmName: string;
  dataSourceId: number;
  dataSourceName: string;
  agentId: number | null;
  agentName: string | null;
  status: string;
  rowsFetched: number;
  startedAt: string;
  completedAt: string | null;
  durationMs: number | null;
  errorMessage: string | null;
}

export const monitoringApi = {
  getDashboard: async (): Promise<DashboardMetrics> => {
    const response = await apiClient.get('/api/monitoring/dashboard');
    return response.data;
  },

  getFirmMetrics: async (firmId: number): Promise<FirmMetrics> => {
    const response = await apiClient.get(`/api/monitoring/firms/${firmId}`);
    return response.data;
  },

  getSyncHistory: async (params?: {
    firmId?: number;
    dataSourceId?: number;
    status?: string;
    from?: string;
    to?: string;
    page?: number;
    pageSize?: number;
  }): Promise<SyncHistoryResponse> => {
    const response = await apiClient.get('/api/monitoring/sync-history', { params });
    return response.data;
  },
};

// ── Kullanım analitiği (S188) ───────────────────────────────────────────────
export interface KullanimKullanici {
  kullaniciId: number;
  kullanici: string;
  eposta: string;
  firmaId: number | null;
  firma: string | null;
  aktif: boolean;
  /** `users.last_login_at` — TEK damga, geçmiş değil. */
  sonGiris: string | null;
  /** Denetimdeki son işlem. 🔴 `sonGiris`ten AYRI: refresh token oturumu haftalarca
   *  ayakta tutuyor (ölçüldü: en uzun fark 54 gün). */
  sonIslem: string | null;
  sorgu: number;
  giris: number;
  aktifGun: number;
}

export interface KullanimRapor {
  kaynakId: number;
  rapor: string | null;
  /** Kaynak silinmiş — `kaynak_id` bilerek yabancı anahtar değil. */
  silinmis: boolean;
  firmaId: number | null;
  calisma: number;
  kullanici: number;
  sonCalisma: string;
}

export interface KullanimGun {
  gun: string;
  istek: number;
  kullanici: number;
}

export interface KullanimAnalitigi {
  gun: number;
  denetimBasladi: string | null;
  /** 🔴 Giriş kimliği bu tarihte yazılmaya BAŞLADI; öncesi ölçülmedi (geçmiş DOLMAZ). */
  girisKaydiBasladi: string | null;
  /** 🔴 Rapor kırılımı bu tarihte yazılmaya BAŞLADI; öncesi ölçülmedi. */
  raporKaydiBasladi: string | null;
  kullanicilar: KullanimKullanici[];
  raporlar: KullanimRapor[];
  gunluk: KullanimGun[];
}

export const kullanimApi = {
  getir: async (gun = 30): Promise<KullanimAnalitigi> => {
    const response = await apiClient.get('/api/monitoring/kullanim', { params: { gun } });
    return response.data;
  },
};
